/**
 * VendasTable Component - Tabela de Vendas
 */

import { useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Eye, Pencil, Trash2, Plus, Search, ArrowUpDown, ArrowUp, ArrowDown, Flag } from "lucide-react";
import { Venda } from "@/types/venda";
import { useSession } from "@/state/session";
import { can } from "@/lib/rbac";

interface VendasTableProps {
  vendas: Venda[];
  onView: (venda: Venda) => void;
  onEdit: (venda: Venda) => void;
  onDelete: (venda: Venda) => void;
  onCreate: () => void;
}

export function VendasTable({
  vendas,
  onView,
  onEdit,
  onDelete,
  onCreate,
}: VendasTableProps) {
  const [search, setSearch] = useState("");
  const [sortConfig, setSortConfig] = useState<{
    key: string;
    direction: "asc" | "desc";
  }>({ key: "numero_venda", direction: "desc" });

  const { user } = useSession();
  const userRole = user?.role;

  const canEdit = userRole && can(userRole, "vendas", "edit");
  const canDelete = userRole && can(userRole, "vendas", "delete");
  const canCreate = userRole && can(userRole, "vendas", "create");

  const filteredVendas = vendas.filter((venda) => {
    const searchLower = search.toLowerCase();
    return (
      venda.numero_venda?.toString().includes(searchLower) ||
      venda.cliente?.nome?.toLowerCase().includes(searchLower) ||
      venda.status_pagamento.toLowerCase().includes(searchLower)
    );
  });

  const handleSort = (key: string) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
    }));
  };

  const sortedVendas = [...filteredVendas].sort((a, b) => {
    let aVal: any;
    let bVal: any;

    if (sortConfig.key === "cliente") {
      aVal = a.cliente?.nome || "";
      bVal = b.cliente?.nome || "";
    } else if (sortConfig.key === "data_venda") {
      aVal = new Date(a.data_venda).getTime();
      bVal = new Date(b.data_venda).getTime();
    } else if (sortConfig.key === "margem_lucro") {
      aVal = a.margem_lucro ?? -Infinity;
      bVal = b.margem_lucro ?? -Infinity;
    } else if (sortConfig.key === "lucro_valor") {
      aVal = a.lucro_valor ?? -Infinity;
      bVal = b.lucro_valor ?? -Infinity;
    } else {
      aVal = (a as any)[sortConfig.key];
      bVal = (b as any)[sortConfig.key];
    }

    if (aVal == null) return 1;
    if (bVal == null) return -1;

    if (aVal < bVal) return sortConfig.direction === "asc" ? -1 : 1;
    if (aVal > bVal) return sortConfig.direction === "asc" ? 1 : -1;
    return 0;
  });

  const getSortIcon = (key: string) => {
    if (sortConfig.key !== key) return <ArrowUpDown className="h-4 w-4" />;
    return sortConfig.direction === "asc" ? (
      <ArrowUp className="h-4 w-4" />
    ) : (
      <ArrowDown className="h-4 w-4" />
    );
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(value);
  };

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString("pt-BR");
  };

  const getStatusBadgeClass = (status: string) => {
    switch (status) {
      case "Pago":
        return "bg-success/10 text-success";
      case "Pendente":
        return "bg-warning/10 text-warning";
      case "Cancelado":
        return "bg-destructive/10 text-destructive";
      default:
        return "bg-muted text-muted-foreground";
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-4">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Buscar vendas..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>
        {canCreate && (
          <Button onClick={onCreate}>
            <Plus className="mr-2 h-4 w-4" />
            Nova Venda
          </Button>
        )}
      </div>

      <div className="border rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleSort("numero_venda")}
                  className="hover:bg-accent"
                >
                  Nº Venda
                  {getSortIcon("numero_venda")}
                </Button>
              </TableHead>
              <TableHead>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleSort("data_venda")}
                  className="hover:bg-accent"
                >
                  Data
                  {getSortIcon("data_venda")}
                </Button>
              </TableHead>
              <TableHead>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleSort("cliente")}
                  className="hover:bg-accent"
                >
                  Cliente
                  {getSortIcon("cliente")}
                </Button>
              </TableHead>
              <TableHead className="text-right">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleSort("lucro_valor")}
                  className="hover:bg-accent ml-auto"
                >
                  Lucro R$
                  {getSortIcon("lucro_valor")}
                </Button>
              </TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleSort("valor_total")}
                  className="hover:bg-accent ml-auto"
                >
                  Valor Total
                  {getSortIcon("valor_total")}
                </Button>
              </TableHead>
              <TableHead className="text-right">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleSort("margem_lucro")}
                  className="hover:bg-accent ml-auto"
                >
                  Margem %
                  {getSortIcon("margem_lucro")}
                </Button>
              </TableHead>
              <TableHead className="text-right">Ações</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {sortedVendas.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} className="text-center text-muted-foreground">
                  Nenhuma venda encontrada
                </TableCell>
              </TableRow>
            ) : (
              sortedVendas.map((venda) => (
                <TableRow key={venda.id_venda}>
                  <TableCell className="font-medium">
                    #{venda.numero_venda}
                  </TableCell>
                  <TableCell>{formatDate(venda.data_venda)}</TableCell>
                  <TableCell>{venda.cliente?.nome || "Cliente avulso"}</TableCell>
                  <TableCell className="text-right font-medium">
                    {venda.lucro_valor != null ? (
                      <span className={venda.lucro_valor >= 0 ? "text-success" : "text-destructive"}>
                        {formatCurrency(venda.lucro_valor)}
                      </span>
                    ) : (
                      <span className="text-muted-foreground">-</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <span
                      className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusBadgeClass(
                        venda.status_pagamento
                      )}`}
                    >
                      {venda.status_pagamento === "Pendente" && (
                        <Flag className="h-3 w-3 text-red-500" fill="currentColor" />
                      )}
                      {venda.status_pagamento}
                    </span>
                  </TableCell>
                  <TableCell className="text-right font-medium">
                    {formatCurrency(venda.valor_total)}
                  </TableCell>
                  <TableCell className="text-right">
                    {venda.margem_lucro != null ? (
                      <span
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          venda.margem_lucro >= 30
                            ? "bg-success/10 text-success"
                            : venda.margem_lucro >= 15
                            ? "bg-warning/10 text-warning"
                            : "bg-destructive/10 text-destructive"
                        }`}
                      >
                        {venda.margem_lucro.toFixed(1)}%
                      </span>
                    ) : (
                      <span className="text-muted-foreground">-</span>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-2">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => onView(venda)}
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                      {canEdit && (
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => onEdit(venda)}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                      )}
                      {canDelete && (
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => onDelete(venda)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        </div>
      </div>
    </div>
  );
}
